/**
 * useActionRequests Hook
 * 
 * Hook per gestire le richieste sensibili (modifica, cancellazione, assistenza).
 * 
 * REGOLA: Le richieste si possono creare SOLO con accesso verificato.
 * 
 * DISTINZIONE:
 * - Stato item ≠ Stato request
 * - Le richieste NON cambiano lo stato dell'item
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Tipi di richiesta
 */
export const REQUEST_TYPES = {
  CHANGE_REQUEST: 'change_request',
  CANCELLATION_REQUEST: 'cancellation_request',
  SUPPORT_REQUEST: 'support_request',
  DOCUMENT_HELP_REQUEST: 'document_help_request'
};

/**
 * Stati della richiesta
 */
export const REQUEST_STATUS = {
  SUBMITTED: 'submitted',
  UNDER_REVIEW: 'under_review',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  RESOLVED: 'resolved',
  CANCELLED_BY_USER: 'cancelled_by_user'
};

/**
 * Hook per gestire le action requests
 * @param {string} code - Codice del journey
 * @param {string} sessionToken - Token di sessione verificata
 * @returns {Object} Dati e funzioni per gestire le richieste
 */
export function useActionRequests(code, sessionToken) {
  const [requestsData, setRequestsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch requests
  const fetchRequests = useCallback(async () => {
    if (!code) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const headers = {};
      if (sessionToken) {
        headers['X-Session-Token'] = sessionToken;
      }

      const response = await fetch(`${API_URL}/api/journey/${code}/requests`, {
        headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch requests: ${response.status}`);
      }

      const data = await response.json();
      setRequestsData(data);
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [code, sessionToken]);

  // Initial fetch
  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Create request
  const createRequest = useCallback(async (itemId, requestType, message, payload = {}) => {
    if (!code || !sessionToken) {
      return { success: false, message: 'Accesso verificato richiesto' };
    }

    try {
      setSubmitting(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/journey/${code}/requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken
        },
        body: JSON.stringify({
          journey_code: code,
          item_id: itemId,
          request_type: requestType,
          message,
          payload
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh requests list
        await fetchRequests();
        return data;
      } else {
        throw new Error(data.detail || data.message || 'Errore nella creazione');
      }
    } catch (err) {
      console.error('Error creating request:', err);
      setError(err.message);
      return { success: false, message: err.message };
    } finally {
      setSubmitting(false);
    }
  }, [code, sessionToken, fetchRequests]);

  // Cancel request
  const cancelRequest = useCallback(async (requestId) => {
    if (!code || !sessionToken) {
      return { success: false, message: 'Accesso verificato richiesto' };
    }

    try {
      const response = await fetch(`${API_URL}/api/journey/${code}/requests/${requestId}/cancel`, {
        method: 'POST',
        headers: {
          'X-Session-Token': sessionToken
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        await fetchRequests();
        return data;
      } else {
        throw new Error(data.detail || data.message || 'Errore nell\'annullamento');
      }
    } catch (err) {
      console.error('Error cancelling request:', err);
      return { success: false, message: err.message };
    }
  }, [code, sessionToken, fetchRequests]);

  // Get requests for specific item
  const getRequestsForItem = useCallback((itemId) => {
    if (!requestsData?.requests) return [];
    return requestsData.requests.filter(r => r.item_id === itemId);
  }, [requestsData]);

  // Check if item has pending requests
  const hasPendingRequestForItem = useCallback((itemId) => {
    const itemRequests = getRequestsForItem(itemId);
    return itemRequests.some(r => 
      r.status === REQUEST_STATUS.SUBMITTED || 
      r.status === REQUEST_STATUS.UNDER_REVIEW
    );
  }, [getRequestsForItem]);

  // Computed values
  const requests = useMemo(() => requestsData?.requests || [], [requestsData]);
  const summary = useMemo(() => requestsData?.summary || { total: 0, pending: 0 }, [requestsData]);
  const isVerified = useMemo(() => requestsData?.verified === true, [requestsData]);
  const typeDisplay = useMemo(() => requestsData?.type_display || {}, [requestsData]);
  const statusDisplay = useMemo(() => requestsData?.status_display || {}, [requestsData]);

  return {
    // Data
    requestsData,
    requests,
    summary,
    loading,
    error,
    submitting,
    
    // Computed
    isVerified,
    typeDisplay,
    statusDisplay,
    
    // Item helpers
    getRequestsForItem,
    hasPendingRequestForItem,
    
    // Actions
    createRequest,
    cancelRequest,
    reload: fetchRequests
  };
}

export default useActionRequests;
